import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FolderOpen, FolderClosed, Mail, MailOpen, Sparkles, ScrollText } from 'lucide-react';
import { ContactModal } from './ContactModal';
import { AnimatedLogo } from './AnimatedLogo';
const HandDrawnCircle = ({
  visible
}: {
  visible: boolean;
}) => <svg className="absolute -inset-x-[10px] -inset-y-[6px] w-[calc(100%+20px)] h-[calc(100%+12px)] pointer-events-none" viewBox="0 0 100 50" fill="none" preserveAspectRatio="none" data-id="element-81">
    <motion.path d="M 50 2 C 72 -1, 96 6, 97 22 C 98 38, 78 48, 50 48 C 22 48, 2 40, 3 24 C 4 8, 28 1, 50 2 Z" stroke="#3A6B4A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" initial={{
    pathLength: 0,
    opacity: 0
  }} animate={{
    pathLength: visible ? 1 : 0,
    opacity: visible ? 1 : 0
  }} transition={{
    pathLength: {
      duration: 0.4,
      ease: 'easeOut'
    },
    opacity: {
      duration: 0.15
    }
  }} data-id="element-82" />
  </svg>;
const HandDrawnUnderline = ({
  visible
}: {
  visible: boolean;
}) => <svg className="absolute -bottom-[4px] left-0 w-full h-[8px] pointer-events-none" viewBox="0 0 100 10" fill="none" preserveAspectRatio="none" data-id="element-83">
    <motion.path d="M 2 6 C 15 3, 25 8, 40 5 C 55 2, 65 7, 80 4 C 88 3, 95 5, 98 5" stroke="#3A6B4A" strokeWidth="2.5" strokeLinecap="round" fill="none" initial={{
    pathLength: 0,
    opacity: 0
  }} animate={{
    pathLength: visible ? 1 : 0,
    opacity: visible ? 1 : 0
  }} transition={{
    pathLength: {
      duration: 0.35,
      ease: 'easeOut'
    },
    opacity: {
      duration: 0.15
    }
  }} data-id="element-84" />
  </svg>;
interface HeaderProps {
  onNavigateHome?: () => void;
  onNavigatePlay?: () => void;
  onNavigateAbout?: () => void;
  activePage?: 'home' | 'case-study' | 'play' | 'about';
}
export function Header({
  onNavigateHome,
  onNavigatePlay,
  onNavigateAbout,
  activePage = 'home'
}: HeaderProps) {
  const [aboutHovered, setAboutHovered] = useState(false);
  const [workHovered, setWorkHovered] = useState(false);
  const [playHovered, setPlayHovered] = useState(false);
  const [contactHovered, setContactHovered] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [logoState, setLogoState] = useState<'default' | 'hover' | 'pressed'>('default');
  const isWorkActive = activePage === 'home';
  const isPlayActive = activePage === 'play';
  const isAboutActive = activePage === 'about';
  useEffect(() => {
    if (!isWorkActive) setLogoState('default');
  }, [isWorkActive]);
  const handleWorkClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onNavigateHome) {
      onNavigateHome();
      setTimeout(() => {
        document.getElementById('work')?.scrollIntoView({
          behavior: 'smooth'
        });
      }, 100);
    }
  };
  const handlePlayClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onNavigatePlay) onNavigatePlay();
  };
  const handleAboutClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onNavigateAbout) onNavigateAbout();
  };
  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setContactOpen(true);
  };
  const navItems = [{
    id: 'work',
    label: 'Work',
    icon: workHovered || isWorkActive ? <FolderOpen size={20} data-id="element-85" /> : <FolderClosed size={20} data-id="element-86" />,
    iconSmall: workHovered || isWorkActive ? <FolderOpen size={18} data-id="element-87" /> : <FolderClosed size={18} data-id="element-88" />,
    active: isWorkActive,
    onClick: handleWorkClick,
    onMouseEnter: () => setWorkHovered(true),
    onMouseLeave: () => setWorkHovered(false)
  }, {
    id: 'play',
    label: 'Play',
    icon: <Sparkles size={20} data-id="element-89" />,
    iconSmall: <Sparkles size={18} data-id="element-90" />,
    active: isPlayActive,
    onClick: handlePlayClick,
    onMouseEnter: () => setPlayHovered(true),
    onMouseLeave: () => setPlayHovered(false)
  }, {
    id: 'about',
    label: 'About',
    icon: <ScrollText size={20} data-id="element-91" />,
    iconSmall: <ScrollText size={18} data-id="element-92" />,
    active: isAboutActive,
    onClick: handleAboutClick,
    onMouseEnter: () => setAboutHovered(true),
    onMouseLeave: () => setAboutHovered(false)
  }, {
    id: 'contact',
    label: 'Contact',
    icon: contactHovered ? <MailOpen size={20} data-id="element-93" /> : <Mail size={20} data-id="element-94" />,
    iconSmall: contactHovered ? <MailOpen size={18} data-id="element-95" /> : <Mail size={18} data-id="element-96" />,
    active: false,
    onClick: handleContactClick,
    onMouseEnter: () => setContactHovered(true),
    onMouseLeave: () => setContactHovered(false)
  }];
  return <>
      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} data-id="element-97" />

      {/* ===== MOBILE: Fixed bottom tab bar ===== */}
      <motion.nav initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5,
      ease: 'easeOut'
    }} className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-t border-[#E8E4DF]" data-id="element-98">
        <div className="flex items-center justify-around h-16 px-2" data-id="element-99">
          {navItems.map(item => <a key={item.id} href={`#${item.id}`} onClick={item.onClick} className={`flex flex-col items-center justify-center gap-0.5 flex-1 py-2 transition-colors duration-200 ${item.active ? 'text-[#1A1A1A]' : 'text-[#A09A94]'}`} data-id="element-100">
              <span className="w-5 h-5 flex items-center justify-center" data-id="element-101">
                {item.iconSmall}
              </span>
              <span className={`text-[10px] font-medium ${item.active ? 'font-bold' : ''}`} data-id="element-102">
                {item.label}
              </span>
            </a>)}
        </div>
        {/* Safe area padding for phones with home indicator */}
        <div className="h-[env(safe-area-inset-bottom)]" data-id="element-103" />
      </motion.nav>

      {/* ===== TABLET: Narrow icon-only sidebar (md to lg) ===== */}
      <motion.aside initial={{
      opacity: 0,
      x: -20
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      duration: 0.8,
      ease: 'easeOut'
    }} className="hidden md:flex lg:hidden fixed left-0 top-0 h-screen w-16 bg-[#F8F6F3] border-r border-[#E8E4E0] flex-col justify-between items-center py-8 z-50" data-id="element-104">
        <div className="flex flex-col items-center" data-id="element-105">
          <div onClick={!isWorkActive ? onNavigateHome : undefined} onMouseEnter={() => !isWorkActive && setLogoState('hover')} onMouseLeave={() => !isWorkActive && setLogoState('default')} onMouseDown={() => !isWorkActive && setLogoState('pressed')} onMouseUp={() => !isWorkActive && setLogoState('hover')} className={`${isWorkActive ? '' : 'cursor-pointer'}`} data-id="element-106">
            <AnimatedLogo state={isWorkActive ? 'default' : logoState} className="h-8 w-8" data-id="element-107" />
          </div>

          <nav className="mt-8" data-id="element-108">
            <ul className="flex flex-col items-center gap-5" data-id="element-109">
              {navItems.map(item => <li key={item.id} data-id="element-110">
                  <a href={`#${item.id}`} onClick={item.onClick} onMouseEnter={item.onMouseEnter} onMouseLeave={item.onMouseLeave} className={`relative w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 ${item.active ? 'text-[#1A1A1A] bg-[#E8E4DF]' : 'text-[#A09A94] hover:text-[#1A1A1A] hover:bg-[#E8E4DF]/50'}`} title={item.label} data-id="element-111">
                    {item.icon}
                  </a>
                </li>)}
            </ul>
          </nav>
        </div>

        <p className="text-[9px] text-[#A09A94] [writing-mode:vertical-lr] rotate-180" data-id="element-112">
          © 2025
        </p>
      </motion.aside>

      {/* ===== DESKTOP: Full sidebar with icon + label (lg and up) ===== */}
      <motion.aside initial={{
      opacity: 0,
      x: -20
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      duration: 0.8,
      ease: 'easeOut'
    }} className="hidden lg:flex fixed left-0 top-0 h-screen w-56 bg-[#F8F6F3] border-r border-[#E8E4E0] flex-col justify-between px-8 py-12 z-50" data-id="element-113">
        <div data-id="element-114">
          <div onClick={!isWorkActive ? onNavigateHome : undefined} onMouseEnter={() => !isWorkActive && setLogoState('hover')} onMouseLeave={() => !isWorkActive && setLogoState('default')} onMouseDown={() => !isWorkActive && setLogoState('pressed')} onMouseUp={() => !isWorkActive && setLogoState('hover')} className={`${isWorkActive ? '' : 'cursor-pointer'}`} data-id="element-115">
            <AnimatedLogo state={isWorkActive ? 'default' : logoState} className="h-12 w-12" data-id="element-116" />
          </div>

          <nav className="mt-8" data-id="element-117">
            <ul className="flex flex-col gap-4 text-[#6B6560] font-medium" data-id="element-118">
              {/* Work */}
              <li data-id="element-119">
                <a href="#work" onClick={handleWorkClick} onMouseEnter={() => setWorkHovered(true)} onMouseLeave={() => setWorkHovered(false)} className={`flex items-center gap-2.5 transition-all duration-300 ${isWorkActive ? 'text-[#1A1A1A] font-bold translate-x-2' : 'hover:text-[#1A1A1A]'}`} data-id="element-120">
                  {isWorkActive && <span className="w-[6px] h-[6px] rounded-full bg-[#3A6B4A] shrink-0 -ml-[16px]" data-id="element-121" />}
                  <span className="w-4 h-4 flex items-center justify-center shrink-0" data-id="element-122">
                    {workHovered || isWorkActive ? <FolderOpen size={16} data-id="element-123" /> : <FolderClosed size={16} data-id="element-124" />}
                  </span>
                  Work
                </a>
              </li>

              {/* Play */}
              <li data-id="element-125">
                <a href="#play" onClick={handlePlayClick} onMouseEnter={() => setPlayHovered(true)} onMouseLeave={() => setPlayHovered(false)} className={`flex items-center gap-2.5 transition-all duration-300 ${isPlayActive ? 'text-[#1A1A1A] font-bold' : 'hover:text-[#1A1A1A]'}`} data-id="element-126">
                  <span className="w-4 h-4 flex items-center justify-center shrink-0" data-id="element-127">
                    <Sparkles size={16} data-id="element-128" />
                  </span>
                  <span className="relative" data-id="element-129">
                    Play
                    <HandDrawnCircle visible={playHovered && !isPlayActive} data-id="element-130" />
                    <HandDrawnUnderline visible={isPlayActive} data-id="element-131" />
                  </span>
                </a>
              </li>

              {/* About */}
              <li data-id="element-132">
                <a href="#about" onClick={handleAboutClick} onMouseEnter={() => setAboutHovered(true)} onMouseLeave={() => setAboutHovered(false)} className={`flex items-center gap-2.5 transition-all duration-300 ${isAboutActive ? 'text-[#1A1A1A] font-bold translate-x-2' : 'hover:text-[#1A1A1A]'}`} data-id="element-133">
                  {isAboutActive && <span className="w-[6px] h-[6px] rounded-full bg-[#3A6B4A] shrink-0 -ml-[16px]" data-id="element-134" />}
                  <span className="w-4 h-4 flex items-center justify-center shrink-0" data-id="element-135">
                    <ScrollText size={16} data-id="element-136" />
                  </span>
                  About
                </a>
              </li>

              {/* Contact */}
              <li data-id="element-137">
                <a href="#contact" onClick={handleContactClick} onMouseEnter={() => setContactHovered(true)} onMouseLeave={() => setContactHovered(false)} className="flex items-center gap-2.5 hover:text-[#1A1A1A] transition-colors duration-300" data-id="element-138">
                  <span className="w-4 h-4 flex items-center justify-center shrink-0" data-id="element-139">
                    {contactHovered ? <MailOpen size={16} data-id="element-140" /> : <Mail size={16} data-id="element-141" />}
                  </span>
                  Contact
                </a>
              </li>
            </ul>
          </nav>
        </div>

        
      </motion.aside>
    </>;
}